<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

import { XDomainsStore } from "@Modules/urls/Store/XDomainsStore";
import { useCurrentOrganization } from "@Modules/teams/composables/useCurrentOrganization";
import DomainsApi from "@Modules/teams/Api/DomainsApi";
import { Domain } from "@Modules/urls/Data/Models/DomainModel";
import { XUrlsStore } from "@Modules/urls/Store/XUrlsStore";

import V2BulkForm from "./V2BulkForm.vue";
import V2SingleForm from "./V2SingleForm.vue";

//
// Setup
const domainStore = XDomainsStore();
const { currentOrganization, currentWorkspace } = useCurrentOrganization();
const urlStore = XUrlsStore();
const domainsApi = DomainsApi();

//
// Data
const selectedMode = ref<"single" | "bulk">("single");

//
// Computed
const availableTeams = computed(() => currentOrganization.value?.publicTeams() ?? []);

const filteredOrgDomains = computed<Domain[]>(() => {
  const orgDomains = domainsApi.domains.value;
  const teamSlug = currentWorkspace.value?.slug;

  return orgDomains.filter(
    (d) => d.for_all_teams || d.teams?.some(t => t.slug === teamSlug),
  );
});

const teamAvailableDomains = computed<Domain[]>(() => {
  const storeDomains = domainStore.availableDomains;
  const orgDomainNames = filteredOrgDomains.value.map((d) => d.domain);

  // Combine: store domains + any org domains not already in store
  const combined: Domain[] = [...storeDomains];
  for (const name of orgDomainNames) {
    if (!combined.some((d) => d.domain === name)) {
      combined.push({ domain: name, isBranded: false } as Domain);
    }
  }

  return combined;
});

const selectableDomains = computed<{ value: string; name: string }[]>(() =>
  teamAvailableDomains.value.map((d) => ({ value: d.domain, name: d.domain })),
);

//
// Methods
function selectMode(mode: "single" | "bulk") {
  selectedMode.value = mode;
}

async function fetchDomains(): Promise<void> {
  if (!currentOrganization.value?.slug) {
    return;
  }
  await domainsApi.fetchOrgDomains({ orgSlug: currentOrganization.value.slug });
}

//
// Watchers
watch(currentWorkspace, () => fetchDomains());

onMounted(() => fetchDomains());
onUnmounted(() => urlStore.clearErrors());

//
// Exposed Test Vars
defineExpose({ selectedMode, urlStore, domainsApi, teamAvailableDomains, selectableDomains });
</script>

<template>
  <div class="_create_form v2-create-form">
    <div
      v-if="currentOrganization"
      class="v2-create-form__organization _organization"
    >
      {{ currentOrganization.name }}
    </div>

    <div class="v2-create-form__toggle">
      <button
        type="button"
        class="v2-create-form__toggle-option _single_link"
        :class="{ 'v2-create-form__toggle-option--active': selectedMode === 'single' }"
        @click="selectMode('single')"
      >
        Single Link
      </button>
      <button
        type="button"
        class="v2-create-form__toggle-option _bulk_link"
        :class="{ 'v2-create-form__toggle-option--active': selectedMode === 'bulk' }"
        @click="selectMode('bulk')"
      >
        Bulk Link
      </button>
    </div>

    <V2SingleForm
      v-if="selectedMode === 'single' && currentOrganization"
      :organization="currentOrganization"
      :available-teams="availableTeams"
      :team="currentWorkspace"
      :submit="urlStore.createUrl"
      :errors="urlStore.errors"
      :loading="urlStore.sending"
      :available-domains="teamAvailableDomains"
      :selectable-domains="selectableDomains"
    />

    <V2BulkForm
      v-else
      :organization="currentOrganization?.slug ?? ''"
      :available-teams="availableTeams"
      :team="currentWorkspace?.slug ?? ''"
      :available-domains="teamAvailableDomains"
      :selectable-domains="selectableDomains"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.v2-create-form {
  &__organization {
    margin-bottom: spacing.$s;
    font-size: typography.$s-font-size;
    font-weight: typography.$font-weight-semi-bold;
    color: color.$text;
  }

  &__toggle {
    display: flex;
    background-color: color.$primary;
    border-radius: 25px;
    padding: 4px;
    margin-bottom: spacing.$s;
  }

  &__toggle-option {
    flex: 1;
    padding: 10px 20px;
    border: none;
    background: transparent;
    color: #fff;
    font-size: typography.$s-font-size;
    font-weight: typography.$font-weight-semi-bold;
    cursor: pointer;
    border-radius: 21px;
    transition: background-color 0.2s ease, color 0.2s ease;

    &--active {
      background-color: #fff;
      color: color.$primary;
    }

    &:hover:not(&--active) {
      background-color: rgba(255, 255, 255, 0.1);
    }

    &:focus {
      outline: none;
    }
  }
}
</style>
